<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex flex-col md:flex-row overflow-hidden">
    <ClientSidebar />

    <main class="flex-1 flex flex-col h-full overflow-hidden">

      <!-- Header -->
      <header class="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">My Tasks</h2>
          <p class="text-xs text-slate-500">Track your outstanding to-dos assigned by your CPA.</p>
        </div>
        <div class="flex items-center gap-2">
          <span class="px-3 py-1.5 rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
            {{ openCount }} open
          </span>
          <span class="px-3 py-1.5 rounded-full bg-green-50 text-xs font-semibold text-green-600">
            {{ doneCount }} completed
          </span>
        </div>
      </header>

      <div class="flex-1 overflow-y-auto p-4 md:p-8">

        <!-- Loading -->
        <div v-if="loading" class="flex flex-col items-center justify-center h-full gap-4">
          <svg class="animate-spin h-10 w-10 text-brand-dark" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-slate-400 text-sm">Loading tasks...</p>
        </div>

        <div v-else class="max-w-[860px] mx-auto space-y-8">

          <!-- Filter tabs -->
          <div class="flex gap-2 border-b border-slate-200">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              @click="activeTab = tab.value"
              class="px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px"
              :class="activeTab === tab.value
                ? 'border-[#282F44] text-[#282F44]'
                : 'border-transparent text-slate-400 hover:text-slate-600'">
              {{ tab.label }}
              <span
                class="ml-1.5 px-1.5 py-0.5 rounded-full text-xs"
                :class="activeTab === tab.value ? 'bg-[#282F44] text-white' : 'bg-slate-100 text-slate-500'">
                {{ tabCount(tab.value) }}
              </span>
            </button>
          </div>

          <!-- Empty state -->
          <div v-if="filteredTasks.length === 0" class="bg-white rounded-2xl border border-slate-200 p-16 flex flex-col items-center text-center">
            <span class="material-symbols-outlined text-slate-300 !text-[48px] mb-3">task_alt</span>
            <p class="text-slate-700 font-semibold mb-1">
              {{ activeTab === 'Completed' ? 'No completed tasks yet' : 'No tasks here' }}
            </p>
            <p class="text-slate-400 text-sm">Your CPA will assign tasks when action is needed.</p>
          </div>

          <!-- Task list -->
          <div v-else class="space-y-3">
            <div
              v-for="task in filteredTasks"
              :key="task.Id"
              class="bg-white rounded-xl border transition-all"
              :class="task.TaskStatus === 'Completed'
                ? 'border-green-100 bg-green-50/30 opacity-70 hover:opacity-100'
                : 'border-slate-200 hover:shadow-sm'">

              <div class="p-5 flex items-start gap-4">

                <!-- ✅ Status toggle button — cycles To-Do → In Progress → Completed -->
                <button
                  @click="toggleTask(task)"
                  :disabled="task._updating"
                  class="mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-50"
                  :class="task.TaskStatus === 'Completed'
                    ? 'bg-green-500 border-green-500'
                    : task.TaskStatus === 'In Progress'
                      ? 'bg-blue-400 border-blue-400'
                      : 'border-slate-300 hover:border-[#282F44]'">
                  <!-- spinner while saving -->
                  <svg v-if="task._updating" class="animate-spin h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <span
                    v-else-if="task.TaskStatus === 'Completed'"
                    class="material-symbols-outlined text-white !text-[12px]">check</span>
                  <span
                    v-else-if="task.TaskStatus === 'In Progress'"
                    class="h-1.5 w-1.5 rounded-full bg-white"></span>
                </button>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between gap-3">
                    <!-- ✅ strikethrough when Completed -->
                    <p
                      class="text-sm font-semibold"
                      :class="task.TaskStatus === 'Completed'
                        ? 'line-through text-slate-400'
                        : 'text-slate-800'">
                      {{ task.Title }}
                    </p>
                    <span
                      class="px-2 py-0.5 rounded-md text-xs font-semibold flex-shrink-0"
                      :class="getPriorityClass(task.Priority)">
                      {{ task.Priority }}
                    </span>
                  </div>

                  <p v-if="task.Description" class="text-xs text-slate-400 mt-1 leading-relaxed line-clamp-2">
                    {{ task.Description }}
                  </p>

                  <div class="flex items-center gap-4 mt-2 flex-wrap">
                    <span class="flex items-center gap-1 text-xs text-slate-400">
                      <span class="material-symbols-outlined !text-[13px]">category</span>
                      {{ task.Category }}
                    </span>
                    <span v-if="task.DueDate" class="flex items-center gap-1 text-xs"
                      :class="isOverdue(task) ? 'text-red-500' : 'text-slate-400'">
                      <span class="material-symbols-outlined !text-[13px]">schedule</span>
                      Due {{ formatDate(task.DueDate) }}
                      <span v-if="isOverdue(task)" class="font-semibold">· Overdue</span>
                    </span>
                    <!-- ✅ status badge uses Completed not Done -->
                    <span
                      class="px-2 py-0.5 rounded-full text-xs font-semibold"
                      :class="getStatusClass(task.TaskStatus)">
                      {{ task.TaskStatus }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Disclaimer -->
      <p class="px-4 md:px-8 lg:px-12 py-4 text-center leading-relaxed" style="font-size:9px; font-style:italic; color:rgba(0,0,0,0.8);">
        <span class="font-semibold not-italic">Disclaimer:</span> By uploading documents to this portal, you acknowledge that while we employ industry-standard security to protect your data, the transmission of information over the internet is not entirely risk-free. ABC TAX ADVISORY utilizes this third-party platform for your convenience but assumes no responsibility for data security or technical issues arising from its use.
      </p>
    </main>
  </div>
</template>

<script>
import ClientSidebar from '../../components/ClientSidebar.vue';

export default {
  name: 'ClientTasks',
  components: { ClientSidebar },

  data() {
    return {
      loading: true,
      tasks: [],
      activeTab: 'Open',
      tabs: [
        { label: 'Open',        value: 'Open' },
        { label: 'In Progress', value: 'In Progress' },
        { label: 'Completed',   value: 'Completed' },  // ✅ was 'Done'
        { label: 'All',         value: 'All' }
      ]
    };
  },

  computed: {
    clientRecord() {
      try {
        const token = sessionStorage.getItem('clientSession');
        if (!token) return {};
        const [data] = token.split('.');
        if (!data) return {};
        const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp || Date.now() > payload.exp) return {};
        return payload;
      } catch { return {}; }
    },

    clientName() {
      return this.clientRecord?.name || '';
    },

    openCount() {
      return this.tasks.filter(t => t.TaskStatus === 'To-Do').length;
    },

    // ✅ was checking 'Done' — now checks 'Completed' to match SharePoint + admin page
    doneCount() {
      return this.tasks.filter(t => t.TaskStatus === 'Completed').length;
    },

    filteredTasks() {
      if (this.activeTab === 'All') return this.tasks;
      if (this.activeTab === 'Open') return this.tasks.filter(t => t.TaskStatus === 'To-Do');
      if (this.activeTab === 'In Progress') return this.tasks.filter(t => t.TaskStatus === 'In Progress');
      if (this.activeTab === 'Completed') return this.tasks.filter(t => t.TaskStatus === 'Completed'); // ✅ was 'Done'
      return this.tasks;
    }
  },

  async mounted() {
    await this.loadTasks();
  },

  methods: {
    async loadTasks() {
      this.loading = true;
      try {
        const raw = await this.$sharepoint.getClientTasksByName(this.clientName);
        // ✅ add _updating flag for spinner — Vue 2 needs $set to be reactive
        this.tasks = raw.map(t => ({ ...t, _updating: false }));
      } catch (error) {
        console.error('Error loading tasks:', error);
        this.tasks = [];
      } finally {
        this.loading = false;
      }
    },

    tabCount(value) {
      if (value === 'All') return this.tasks.length;
      if (value === 'Open') return this.tasks.filter(t => t.TaskStatus === 'To-Do').length;
      if (value === 'In Progress') return this.tasks.filter(t => t.TaskStatus === 'In Progress').length;
      if (value === 'Completed') return this.doneCount; // ✅ was 'Done'
      return 0;
    },

    // ✅ MAIN FIX: cycles To-Do → In Progress → Completed (not 'Done')
    // uses this.$set for Vue 2 reactivity, writes to SharePoint before updating UI
    async toggleTask(task) {
      if (task._updating) return;

      const next = task.TaskStatus === 'To-Do'
        ? 'In Progress'
        : task.TaskStatus === 'In Progress'
          ? 'Completed'   // ✅ was 'Done' — now matches admin page + SharePoint
          : 'To-Do';

      const index = this.tasks.findIndex(t => t.Id === task.Id);
      if (index === -1) return;

      const original = task.TaskStatus;

      // ✅ set spinner reactively
      this.$set(this.tasks, index, { ...this.tasks[index], _updating: true });

      try {
        // ✅ writes to SharePoint first, then updates UI
        await this.$sharepoint.updateTaskStatus(task.Id, next);
        this.$set(this.tasks, index, { ...this.tasks[index], TaskStatus: next, _updating: false });
      } catch (error) {
        console.error('Error updating task:', error);
        // ✅ rollback reactively
        this.$set(this.tasks, index, { ...this.tasks[index], TaskStatus: original, _updating: false });
      }
    },

    isOverdue(task) {
      if (!task.DueDate || task.TaskStatus === 'Completed') return false; // ✅ was 'Done'
      return new Date(task.DueDate) < new Date();
    },

    getPriorityClass(priority) {
      const map = {
        'High':   'bg-red-50 text-red-500',
        'Medium': 'bg-amber-50 text-amber-500',
        'Normal': 'bg-slate-100 text-slate-500',
        'Low':    'bg-green-50 text-green-600'
      };
      return map[priority] || 'bg-slate-100 text-slate-500';
    },

    getStatusClass(status) {
      const map = {
        'To-Do':      'bg-slate-100 text-slate-500',
        'In Progress': 'bg-blue-50 text-blue-600',
        'Completed':  'bg-green-50 text-green-600'  // ✅ was 'Done'
      };
      return map[status] || 'bg-slate-100 text-slate-500';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>